/**
 * Utilities for the window tab bar ordering.
 *
 * `windowTabOrder` lives on `SpaceSettings` as a string[] of window ids in
 * left-to-right display order. This module is the single source of truth for:
 *   - Normalizing the array before persisting (dedupe + filter stale ids)
 *   - Deriving the rendered tab list from the current windows + the order
 *
 * All callers that read or write `windowTabOrder` should go through these
 * helpers so the invariants are enforced in exactly one place.
 */

import type { AnyWindowEntity } from '../../../types/windows';
import type { BaseEntityType } from '../../../types/entities';

/**
 * Strip duplicates and ids that are not in `validIds` from the order array.
 * Preserves the original relative order of ids that survive.
 */
export function normalizeWindowTabOrder(
  order: readonly string[] | undefined | null,
  validIds: Iterable<string>,
): string[] {
  if (!order || order.length === 0) return [];
  const valid = validIds instanceof Set ? validIds : new Set(validIds);
  const seen = new Set<string>();
  const result: string[] = [];
  for (const id of order) {
    if (!valid.has(id)) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    result.push(id);
  }
  return result;
}

/** Returns true when the two id arrays are equal element-wise. */
export function windowTabOrderEquals(
  a: readonly string[] | undefined | null,
  b: readonly string[] | undefined | null,
): boolean {
  const aLen = a?.length ?? 0;
  const bLen = b?.length ?? 0;
  if (aLen !== bLen) return false;
  if (aLen === 0) return true;
  for (let i = 0; i < aLen; i += 1) {
    if (a![i] !== b![i]) return false;
  }
  return true;
}

/**
 * Produce the ordered, render-ready tab list from the raw windows map and
 * the persisted `windowTabOrder`.
 *
 *   1. When `windowTabOrder` is populated, tabs matching ids in the order
 *      appear first in the order's sequence. Any root windows missing from
 *      the order are appended at the end (lazy migration / cross-session race).
 *   2. When it is absent, fall back to the legacy time-based sort so
 *      existing users who have never dragged a tab see no visual change.
 *
 * `rootWindows` must already be filtered to `type === 'window'` and
 * `!isChildWindow`. `allEntities` is only needed for the legacy fallback.
 */
export function computeOrderedTabs(
  rootWindows: AnyWindowEntity[],
  windowTabOrder: readonly string[] | undefined | null,
  allEntities: BaseEntityType[],
): AnyWindowEntity[] {
  if (windowTabOrder && windowTabOrder.length > 0) {
    const byId = new Map(rootWindows.map(w => [w.id, w]));
    const seen = new Set<string>();
    const ordered: AnyWindowEntity[] = [];
    for (const id of windowTabOrder) {
      if (seen.has(id)) continue;
      const w = byId.get(id);
      if (!w) continue;
      seen.add(id);
      ordered.push(w);
    }
    const unordered = rootWindows.filter(w => !seen.has(w.id));
    return [...ordered, ...unordered];
  }

  return [...rootWindows].sort((a, b) => {
    const entityA = a.entityLlmId ? allEntities.find(e => e.llmId === a.entityLlmId) : null;
    const entityB = b.entityLlmId ? allEntities.find(e => e.llmId === b.entityLlmId) : null;
    const timeA = new Date((entityA as any)?.updated_at || (entityA as any)?.created_at || 0).getTime();
    const timeB = new Date((entityB as any)?.updated_at || (entityB as any)?.created_at || 0).getTime();
    return timeA - timeB;
  });
}
